/**
 * partsApi.ts — axios client for the part-order routes (Gap 1/5).
 *
 * The part-order routes live at `/api/parts/...` (NOT `/api/admin/...`), so
 * they must NOT use the default `api` client whose baseURL is
 * `REACT_APP_API_URL + '/admin'` — that produced `/api/admin/parts/orders`
 * → 404. This client roots at `REACT_APP_API_URL` (= `<origin>/api`) and the
 * call sites use bare `/parts/...` paths, resolving to `<origin>/api/parts/...`.
 */

import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';
import { logger } from '../utils/logger';

const partsAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 15000,
});

partsAxios.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

partsAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const refreshResp = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken },
          );
          const { tokens: newTokens } = refreshResp.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return partsAxios(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    if (error.response) {
      logger.error('Parts API Error:', { status: error.response.status });
    }
    return Promise.reject(error);
  },
);

export default partsAxios;
